import type {
  ProductTypeValue,
  PTRDataStoreType,
} from '@app/data-stores/ptr/types';
import type { DeepReadonly, ObjectValues } from '@app/types';
import type { InventoryItem } from '@app/types/shop-bff/types';
import { STATIC_ASSETS_BASE } from '@redbubble/next-seo/constants';
import { ZodTranslatedString } from '../../../../data-stores/ptr/ptr-schemas';
import type {
  BasicConfiguratorOption,
  ColorOption,
} from './FieldConfigurators/types';

const getTextureFromPublicAssets = (texture: string): string => {
  return `${STATIC_ASSETS_BASE}/product-configurator/${texture}.png`;
};

export const PRODUCT_FAMILY_OPTION = 'product-family-option' as const;
export const PRODUCT_TYPE_OPTION = 'product-option' as const;

export const getColorOptionConfig = (
  value: PTRConfiguratorOption['values'][number],
  locale: string
): ColorOption | null => {
  const config = value.config;
  if (!config) return null;

  switch (config.kind) {
    case 'single-color':
      return {
        value: value.equivalentValue,
        defaultText:
          (locale && value.publicName?.[locale]) ||
          value.publicName?.en ||
          value.equivalentValue,
        displayOrder: value.publicDisplayOrder,
        hexColor: config.color?.hexColor || '',
        media: config.color?.texture
          ? getTextureFromPublicAssets(config.color.texture)
          : undefined,
      };

    case 'body-sleeve-color':
      return {
        value: value.equivalentValue,
        defaultText:
          (locale && value.publicName?.[locale]) ||
          value.publicName?.en ||
          value.equivalentValue,
        displayOrder: value.publicDisplayOrder,
        hexColor:
          config.pickerColor?.hexColor || config.bodyColor?.hexColor || '',
        media: config.pickerColor?.texture
          ? getTextureFromPublicAssets(config.pickerColor.texture)
          : undefined,
      };

    default:
      return {
        value: value.equivalentValue,
        defaultText:
          (locale && value.publicName?.[locale]) ||
          value.publicName?.en ||
          value.equivalentValue,
        displayOrder: value.publicDisplayOrder,
        hexColor: '',
        media: undefined,
      };
  }
};

export const getGenericOptionConfig = (
  value: PTRConfiguratorOption['values'][number],
  locale: string
): BasicConfiguratorOption => {
  return {
    value: value.equivalentValue,
    defaultText: getTranslatedString(
      value.publicName,
      value.equivalentValue,
      locale
    ),
    sublabel: getOptionalTranslatedString(value.publicSubLabel, locale),
    displayOrder: value.publicDisplayOrder,
  };
};

export const getTranslatedString = (
  translatedString: ZodTranslatedString,
  fallbackText: string,
  locale: string
): string => {
  return (
    (locale && translatedString?.[locale]) ||
    translatedString?.en ||
    fallbackText
  );
};

/**
 * Like getTranslatedString, but for fields PTR may leave unpopulated — returns
 * undefined rather than falling back to a placeholder, so callers can omit the
 * element entirely.
 */
export const getOptionalTranslatedString = (
  translatedString: ZodTranslatedString | null | undefined,
  locale: string
): string | undefined => {
  if (!translatedString) return undefined;

  return (
    (locale && translatedString[locale]) || translatedString.en || undefined
  );
};

export type ProductFamilyValue = PTRDataStoreType['productFamilies'][string];
export type ProductTypeOption =
  DeepReadonly<ProductTypeValue>['options'][number];
export type ProductFamilyOption = NonNullable<
  ProductFamilyValue['options']
>[number];

export type PTRConfiguratorOption = ProductTypeOption | ProductFamilyOption;

export const byPublicDisplayOrder = (
  a: Pick<PTRConfiguratorOption, 'publicDisplayOrder'>,
  b: Pick<PTRConfiguratorOption, 'publicDisplayOrder'>
) => a.publicDisplayOrder - b.publicDisplayOrder;

const fieldConfigurationOptionEntity = {
  PRODUCT_FAMILY_OPTION,
  PRODUCT_TYPE_OPTION,
} as const;

export type FieldConfigurationOption = PTRConfiguratorOption & {
  entity: ObjectValues<typeof fieldConfigurationOptionEntity>;
};

export type ConfiguratorOption = FieldConfigurationOption & {
  filterValues: Array<{
    value: PTRConfiguratorOption['values'][number];
    inventoryItem: InventoryItem;
  }>;
};

export const sortOptions = (
  productType: DeepReadonly<ProductTypeValue>,
  productFamily: ProductFamilyValue | null
) => {
  const usedKeys = new Set();
  const sortedOptions: FieldConfigurationOption[] = [];

  // Add ordered options from optionsOrder
  for (const orderItem of [...(productFamily?.optionsOrder || [])].sort(
    byPublicDisplayOrder
  )) {
    let sourceOptions: readonly PTRConfiguratorOption[];

    switch (orderItem.kind) {
      case 'product-family-option':
        sourceOptions = productFamily?.options || [];
        break;
      case 'product-option':
        sourceOptions = productType?.options || [];
        break;
      default:
        throw new Error(`Unknown option kind: ${orderItem.kind}`);
    }
    const option = sourceOptions.find((o) => o.key === orderItem.key);
    if (option) {
      sortedOptions.push({ ...option, entity: orderItem.kind });
      usedKeys.add(`${orderItem.kind}:${orderItem.key}`);
    }
  }

  // Add remaining family options sorted by publicDisplayOrder
  for (const option of [...(productFamily?.options || [])].sort(
    byPublicDisplayOrder
  )) {
    if (!usedKeys.has(`${PRODUCT_FAMILY_OPTION}:${option.key}`)) {
      sortedOptions.push({ ...option, entity: PRODUCT_FAMILY_OPTION });
    }
  }

  // Add remaining product options sorted by publicDisplayOrder
  for (const option of [...(productType?.options || [])].sort(
    byPublicDisplayOrder
  )) {
    if (!usedKeys.has(`${PRODUCT_TYPE_OPTION}:${option.key}`)) {
      sortedOptions.push({ ...option, entity: PRODUCT_TYPE_OPTION });
    }
  }

  return sortedOptions;
};
